/* eslint-disable @typescript-eslint/no-unnecessary-condition --
   Proto types map/repeated fields as required but proto3 JSON OMITS empty ones; guards are deliberate. */
/**
 * BUNDLE_GALLERY_V2 media + tag resolution.
 *
 * A bundle gallery is scoped ONLY by each asset's inline `vdas` map (`Record<vdaKey, {values}>`) — unlike a
 * normal-product V2 gallery there are no variant media refs to join on (the proto omits `variants` /
 * `hydratedVariants` entirely). `mediaAxis` names the VDA keys the bundle's media actually varies on, and
 * is the field to read: `imageAxis` was deprecated in favour of it by proto-registry 3.18.0
 * (`storefront-proto-registry` PR #108), so it is only a fallback for a producer still on the old field.
 *
 * **The assets are SORTED, never filtered.** This is the parity rule, and it is not what an earlier
 * revision of this file did (it filtered to the matching assets, with a global-then-everything fallback
 * when nothing matched). Both the non-PL bundle gallery and the V1 PL one run the SAME sort:
 *
 *   `getImagesSortedByColor(bundle.images, selectedColor).slice(0, 10)`
 *   (`productImages.helper.ts:88` — called from `bundle-product-images-legacy.tsx:50` and from
 *   `product-gallery.helpers.ts:621` on its `showAllMedia` bundle branch)
 *
 * which promotes at most TWO selection-matched images to the front and keeps every other image behind them
 * in payload order, then caps the list at ten. A bundle's assets are BUNDLE-level lifestyle shots, so
 * dropping the ones not tagged with the current colour throws away most of the set; and because nothing is
 * dropped, a selection that matches no asset degrades to "payload order" instead of needing the
 * empty-gallery fallback chain the filter approach required (the captured bundle tags its assets across a
 * fraction of the constituents' palette, so that was the common path, not an edge case).
 *
 * Tags follow the same scope. Non-PL builds them with `getFilteredTagsForBundle`
 * (`productImages/utils.ts:5`) — NOT the single-product pipeline: no promo resolution, no hide-list, keep
 * unscoped tags, keep option-scoped tags whose option matches, dedupe by value. See
 * `buildBundleGalleryTags` for why they are mapped `showOnProductPage: false`.
 */

import { MediaType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import Image from '@src/quince-site-common/src/domains/Image'
import ProductImage from '@src/quince-site-common/src/domains/ProductImage'
import type DomainProductMedia from '@src/quince-site-common/src/domains/ProductMedia'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import ProductTag, { PRODUCT_TAG_TYPE } from '@src/quince-site-common/src/domains/ProductTag'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import ProductVideo from '@src/quince-site-common/src/domains/ProductVideo'
import Video from '@src/quince-site-common/src/domains/Video'
import { VIDEO_FORCE_POSITION } from '@src/quince-site-common/src/utils/constants'
import { isProductVideo } from '@src/quince-site-common/src/utils/typeGuards'

import type {
  BundleGalleryV2WidgetData,
  GalleryTag,
  MediaAsset,
  MediaContent,
  ProductGalleryFlags,
  ProductMedia,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'

// eslint-disable-next-line i18next/no-literal-string -- MIME type fallback, not user-facing copy.
const DEFAULT_VIDEO_CONTENT_TYPE = 'video/mp4'
/** Non-PL bundle galleries hard-cap at 10 (`bundle-product-images-legacy.tsx:51`); V2 keeps that. */
const BUNDLE_GALLERY_IMAGE_LIMIT = 10
/** …and front at most TWO selection-matched images (`getImagesSortedByColor`'s `length < 2` guard). */
const BUNDLE_GALLERY_MATCH_LIMIT = 2

/**
 * How the bundle's media and tags are scoped for the current state: the VDA keys the payload says its
 * media varies on, plus the first required child's canonical selection (decision D5 — the constituent the
 * bundle gallery follows, mirroring non-PL where `BundleProductImages` follows `products[0]`).
 */
export interface BundleGalleryScope {
  /** `mediaAxis`, or the deprecated `imageAxis` when a producer still only sends that. */
  axis: readonly string[]
  /** Canonical VDA key → canonical value key. Canonical matters: the asset map speaks value KEYS. */
  selection: Record<string, string>
}

/**
 * The axis to scope on. `mediaAxis` wins whenever the payload carries it; `imageAxis` is the deprecated
 * predecessor (proto 3.18.0 marked it `[deprecated = true]` and added `mediaAxis`), kept only so a
 * producer mid-rollout still scopes correctly. An empty axis means "scope on whatever each asset declares".
 */
export function resolveBundleGalleryAxis(data: Partial<BundleGalleryV2WidgetData> | undefined): readonly string[] {
  const mediaAxis = data?.mediaAxis
  if (mediaAxis?.length) return mediaAxis
  return data?.imageAxis ?? []
}

/** Assets in stable map order, so the gallery order is the server's order. */
function allAssets(media: ProductMedia | undefined): MediaAsset[] {
  return Object.values(media?.mediaAssets ?? {})
}

/** The keys an asset is scoped on that the axis admits. Empty ⇒ the asset is unscoped for this axis. */
function scopedKeysOf(asset: MediaAsset, axis: readonly string[]): string[] {
  const keys = Object.keys(asset.vdas ?? {})
  return axis.length === 0 ? keys : keys.filter(key => axis.includes(key))
}

/**
 * Does this asset positively match the selection? Every scoped key on the axis must be selected AND carry
 * one of the asset's declared values. An UNSCOPED asset does not match: it is not selection-specific, so it
 * belongs with the rest of the set rather than being promoted to the front — exactly non-PL's test, which
 * requires the image to actually carry the selected colour (`image.options.some(...)`).
 */
function assetMatchesSelection(asset: MediaAsset, scope: BundleGalleryScope): boolean {
  const keys = scopedKeysOf(asset, scope.axis)
  if (keys.length === 0) return false
  return keys.every(key => {
    const selected = scope.selection[key]
    if (selected === undefined) return false
    return (asset.vdas?.[key]?.values ?? []).includes(selected)
  })
}

/**
 * The gallery's asset order: up to `BUNDLE_GALLERY_MATCH_LIMIT` selection-matched assets first, then every
 * remaining asset in payload order (including further matches beyond the limit) — the asset-level twin of
 * `getImagesSortedByColor`. Never drops anything, so the gallery cannot be emptied by a selection.
 */
export function sortBundleGalleryAssets(media: ProductMedia | undefined, scope: BundleGalleryScope): MediaAsset[] {
  const matched: MediaAsset[] = []
  const rest: MediaAsset[] = []

  for (const asset of allAssets(media)) {
    if (matched.length < BUNDLE_GALLERY_MATCH_LIMIT && assetMatchesSelection(asset, scope)) {
      matched.push(asset)
    } else {
      rest.push(asset)
    }
  }

  return [...matched, ...rest]
}

/** Contentful asset URLs arrive protocol-relative (`//images.ctfassets.net/…`). */
function withScheme(url: string): string {
  return url.startsWith('//') ? `https:${url}` : url
}

/** The legacy Video atom prepends `https:` itself, so it must be fed a protocol-relative URL. */
function protocolRelative(url: string): string {
  return url.replace(/^https?:/, '')
}

function toImage(content: MediaContent, index: number, toUrl: (url: string) => string = withScheme): Image {
  return new Image({
    id: content.id ?? `bundle-gallery-media-${index}`,
    url: toUrl(content.url ?? ''),
    fileName: content.fileName ?? `bundle-gallery-media-${index}`,
    height: content.height ?? 0,
    width: content.width ?? 0,
    alt: content.alt ?? content.title ?? '',
    title: content.title ?? '',
    description: content.description ?? '',
    // Bundle assets carry no model-size tagging (the non-PL bundle gallery shows none either).
    displayModelInfo: false,
  })
}

/** One resolved asset → domain media. Bundle assets carry no `options`: scoping is the `vdas` map. */
function toDomainMedia(asset: MediaAsset, index: number): DomainProductMedia | null {
  const content = asset.media?.content
  if (!content?.url) return null

  if (asset.media?.type === MediaType.VIDEO) {
    const video = new Video({
      id: content.id ?? `bundle-gallery-video-${index}`,
      url: protocolRelative(content.url),
      fileName: content.fileName ?? `bundle-gallery-video-${index}`,
      contentType: content.contentType ?? DEFAULT_VIDEO_CONTENT_TYPE,
      title: content.title ?? '',
      description: content.description ?? '',
    })
    const thumbnail = content.thumbnail
    const videoPoster = thumbnail?.url ? toImage(thumbnail, index, protocolRelative) : undefined
    return new ProductVideo({ video, options: [], videoPoster })
  }

  return new ProductImage({ image: toImage(content, index), options: [] })
}

/**
 * The gallery's render list for the current scope: assets sorted selection-matched-first, converted to
 * domain media, images capped at the non-PL bundle limit, with the video slotted at
 * `flags.forcedVideoPosition` (falling back to the shared `VIDEO_FORCE_POSITION`) exactly as V1 does.
 *
 * Only IMAGES take part in the sort — matching V1, whose bundle branch sorts `images` and then takes
 * `videos[0]` unconditionally (`product-gallery.helpers.ts:641`), never selection-filtering the video.
 */
export function buildBundleGalleryMedia(
  media: ProductMedia | undefined,
  scope: BundleGalleryScope,
  flags?: ProductGalleryFlags,
): DomainProductMedia[] {
  const sorted = sortBundleGalleryAssets(media, scope)
  const isVideoAsset = (asset: MediaAsset): boolean => asset.media?.type === MediaType.VIDEO

  const resolved = (assets: MediaAsset[]): DomainProductMedia[] =>
    assets
      .map((asset, index) => toDomainMedia(asset, index))
      .filter((item): item is DomainProductMedia => item !== null)

  // `isProductVideo`, not `instanceof`: the media domain constructors `return {...this}`, so every
  // instance is a plain object and `instanceof ProductVideo` is ALWAYS false (the shared gallery helpers
  // use the same guard for the same reason). The asset-level `type` check above is the primary split; this
  // guard keeps the domain-level assertion honest for the slotting below.
  const galleryMedia = resolved(sorted.filter(asset => !isVideoAsset(asset)))
    .filter(item => !isProductVideo(item))
    .slice(0, BUNDLE_GALLERY_IMAGE_LIMIT)

  // Payload order, NOT sort order — the video never participates in selection matching (see above).
  const video = resolved(allAssets(media).filter(isVideoAsset)).find(item => isProductVideo(item))
  if (!video) return galleryMedia

  const flagPosition = flags?.forcedVideoPosition
  const forcedVideoPosition = flagPosition && flagPosition > 0 ? flagPosition : VIDEO_FORCE_POSITION
  const videoIndex = Math.min(forcedVideoPosition - 1, galleryMedia.length)
  galleryMedia.splice(videoIndex, 0, video)
  return galleryMedia
}

/**
 * Gallery tags for the current scope — the V2 port of non-PL `getFilteredTagsForBundle`
 * (`productImages/utils.ts:5`): keep tags with NO options, keep option-scoped tags whose option matches the
 * selection, dedupe by `value`. Deliberately NOT the single-product tag pipeline: non-PL bundles resolve no
 * promos, apply no live-selection applicability and no hide-list, and running that pipeline on a bundle
 * surfaces badges a legacy bundle never shows (see `product-gallery.helpers.ts:664`).
 *
 * The one place V2 can do BETTER than the V1 PL port: `toBundleTags` there had to drop every option-scoped
 * tag outright, because the V1 gallery payload carries no bundle `productOptions` to match an option value
 * against. A V2 bundle publishes its first required child's canonical selection, so a scoped tag is matched
 * for real — the captured payload's only tag is `Best seller` scoped to `color: Down`, which IS the
 * selection.
 *
 * **`showOnProductPage: false` is load-bearing, not cosmetic — a legacy bundle renders NO gallery tags.**
 * `getFilteredTagsForBundle` maps to `{ value, tagType, options }` and never sets the flag, and both
 * renderers gate on it: `tagsToShowOnPdp = productTags.filter(t => t.showOnProductPage)`, then
 * `applicableTags = isTagVisible && !isBundle ? productTags : tagsToShowOnPdp`
 * (`SliderImageWithText.tsx:119,141,238` and `product-gallery-ds.tsx:681,701`). With `isBundle` set, a
 * bundle always lands on `tagsToShowOnPdp`, which is empty — confirmed against production, where the
 * bundle PDP's gallery tag list renders empty. Marking these `true` made PL render tags on every bundle
 * (the V1 widget hit exactly that). Keep this flag and the `isBundle` prop on both renderers in sync:
 * either one alone still shows tags. Flipping it to `true` is the single change if merch ever wants
 * bundle gallery tags — that is a product decision, not a parity one.
 *
 * **No variant-level tag rollup here, by construction — including for `New`.** The single-product V2
 * gallery collapses a SET of per-variant tag lists into one group list via `rollupSvdTags`
 * (`quince-site-common/src/helpers/svd-tag-rollup.ts`); that is where the aggregate `New` is derived, as
 * ALL over the per-variant `New` tag (BE, 2026-08-22: `firstLiveAt` is not on the wire and is not coming,
 * so the per-variant tag IS the input to the aggregate). A bundle has no such set to collapse:
 * `BundleGalleryV2WidgetData` carries BUNDLE-level `GalleryTag[]` and omits `variants` /
 * `hydratedVariants` entirely, so whatever aggregation spans the constituents' variants has already
 * happened server-side. Scope the bundle's own tags by selection (below) and stop there — do not wire the
 * rollup in, and do not chase `firstLiveAt` for it.
 */
export function buildBundleGalleryTags(
  tags: readonly GalleryTag[] | undefined,
  scope: BundleGalleryScope,
): ProductTag[] {
  const seen = new Set<string>()
  const result: ProductTag[] = []

  for (const tag of tags ?? []) {
    const value = tag.value ?? tag.displayName ?? ''
    if (!value || seen.has(value)) continue

    const options = tag.options ?? []
    // `.some`, matching non-PL: one matching option admits the tag.
    const applies = options.length === 0 || options.some(option => scope.selection[option.name ?? ''] === option.value)
    if (!applies) continue

    seen.add(value)
    result.push(
      new ProductTag({
        value,
        displayName: tag.displayName,
        tagType: PRODUCT_TAG_TYPE.PRIMARY,
        // See the note above — a legacy bundle renders no gallery tags, and this is what reproduces that.
        showOnProductPage: false,
        vendor_type: tag.vendorType,
        options: options.map(
          option =>
            new ProductVariantOption({
              name: (option.name ?? '') as ProductOptionName,
              value: option.value,
              displayName: option.displayName,
            }),
        ),
      }),
    )
  }

  return result
}

/* eslint-enable @typescript-eslint/no-unnecessary-condition */
